// CAD-first reading of the stats and companion payloads. The server converts
// every quote with a dated Bank of Canada average and reports what it could
// not convert; these helpers only decide what is displayable. A source-currency
// amount is never promoted to the headline: no CAD estimate means "no estimate".

const finite = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));

// summary.estimate_cad {amount, currency:'CAD', priced_copies, excluded_copies, fx_as_of, stale, partial}
export function cadEstimate(summary) {
  const e = summary && typeof summary === 'object' ? summary.estimate_cad : null;
  if (!e || typeof e !== 'object') return null;
  if (String(e.currency || 'CAD').toUpperCase() !== 'CAD') return null;
  if (!finite(e.amount)) return null;
  const priced = finite(e.priced_copies) ? Number(e.priced_copies) : null;
  const excluded = finite(e.excluded_copies) ? Number(e.excluded_copies) : null;
  return {
    amount: Number(e.amount),
    priced,
    excluded,
    total: priced !== null && excluded !== null ? priced + excluded : null,
    fxAsOf: e.fx_as_of || null,
    stale: e.stale === true,
    partial: e.partial === true || (excluded !== null && excluded > 0)
  };
}

// An entry's CAD estimate, wherever the server put it (stats rows carry it at
// the top level and under price; companion cards under price).
export function entryCad(card) {
  if (!card || typeof card !== 'object') return null;
  const e = (card.estimate_cad && typeof card.estimate_cad === 'object') ? card.estimate_cad
    : (card.price && typeof card.price === 'object' && card.price.estimate_cad && typeof card.price.estimate_cad === 'object') ? card.price.estimate_cad : null;
  if (!e || !finite(e.amount)) return null;
  if (String(e.currency || 'CAD').toUpperCase() !== 'CAD') return null;
  return { amount: Number(e.amount), fxSource: e.fx_source || null, fxAsOf: e.fx_as_of || null, rate: finite(e.fx_rate) ? Number(e.fx_rate) : null, stale: e.stale === true };
}

// Sets with a usable CAD value, largest first, with the share of the largest
// for bar widths. Sets the server could not convert are left out, not zeroed.
export function cadSets(sets, limit = 8) {
  if (!Array.isArray(sets)) return [];
  const rows = sets
    .filter(s => s && String(s.currency || 'CAD').toUpperCase() === 'CAD' && finite(s.value) && Number(s.value) > 0)
    .map(s => ({
      id: s.id, name: s.name || String(s.id || ''), amount: Number(s.value), count: finite(s.count) ? Number(s.count) : null,
      priced: finite(s.estimate_cad?.priced_copies) ? Number(s.estimate_cad.priced_copies) : null,
      excluded: finite(s.estimate_cad?.excluded_copies) ? Number(s.estimate_cad.excluded_copies) : null
    }))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, limit);
  const max = rows.length ? rows[0].amount : 0;
  return rows.map(r => ({ ...r, share: max > 0 ? Math.max(0.02, r.amount / max) : 0 }));
}

// Original-currency lines for the Details disclosure: [{currency, amount}].
export function originalValues(summary) {
  const v = summary && typeof summary === 'object' ? summary.values_by_currency : null;
  if (!v || typeof v !== 'object') return [];
  return Object.entries(v).filter(([c, a]) => /^[A-Z]{3}$/i.test(c) && finite(a)).map(([currency, amount]) => ({ currency: currency.toUpperCase(), amount: Number(amount) })).sort((a, b) => a.currency.localeCompare(b.currency));
}

// summary.fx {source, as_of, rates:{CAD:1, USD, EUR}} → [{currency, rate}] excluding CAD.
export function fxRates(summary) {
  const fx = summary && typeof summary === 'object' ? summary.fx : null;
  if (!fx || typeof fx !== 'object' || !fx.rates || typeof fx.rates !== 'object') return [];
  return Object.entries(fx.rates).filter(([c, r]) => c.toUpperCase() !== 'CAD' && finite(r) && Number(r) > 0).map(([currency, rate]) => ({ currency: currency.toUpperCase(), rate: Number(rate) })).sort((a, b) => a.currency.localeCompare(b.currency));
}
